import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { RotateCcw, Check, ChevronRight, AlertTriangle, Pill, Smartphone, X, ArrowRight, Flame, Sun, Sparkles, Wind, Music } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import {
  recommendCategories,
  getTopProduct,
  TYPE_LABELS,
  TYPE_REASONS,
  TYPE_COLORS,
  WELLNESS_SUITE,
  shouldRecommendWellnessSuite,
} from './data';
import ProductBottle from './ProductBottle';
import AlternativesSheet from './AlternativesSheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inline SVG icon for the Wellness Suite card — lotus/spa motif.
const WellnessSuiteIcon = () => (
  <svg viewBox="0 0 56 56" width={52} height={52} aria-hidden="true" fill="none">
    {/* Outer ambient ring */}
    <circle cx="28" cy="28" r="24" stroke="#d97736" strokeWidth="1" opacity="0.2" />
    {/* Inner ring */}
    <circle cx="28" cy="28" r="16" stroke="#d97736" strokeWidth="1.5" opacity="0.35" />
    {/* 4 petals — lotus motif */}
    <path d="M28 10 C24 16,24 22,28 24 C32 22,32 16,28 10Z" fill="#d97736" opacity="0.55" />
    <path d="M28 32 C24 34,24 40,28 46 C32 40,32 34,28 32Z" fill="#d97736" opacity="0.55" />
    <path d="M10 28 C16 24,22 24,24 28 C22 32,16 32,10 28Z" fill="#d97736" opacity="0.55" />
    <path d="M32 28 C34 24,40 24,46 28 C40 32,34 32,32 28Z" fill="#d97736" opacity="0.55" />
    {/* Diagonal petals (lighter) */}
    <path d="M16 16 C18 20,22 22,24 24 C22 22,20 18,16 16Z" fill="#d97736" opacity="0.3" />
    <path d="M40 16 C38 20,34 22,32 24 C34 22,36 18,40 16Z" fill="#d97736" opacity="0.3" />
    <path d="M16 40 C18 36,22 34,24 32 C22 34,20 38,16 40Z" fill="#d97736" opacity="0.3" />
    <path d="M40 40 C38 36,34 34,32 32 C34 34,36 38,40 40Z" fill="#d97736" opacity="0.3" />
    {/* Center glow */}
    <circle cx="28" cy="28" r="5" fill="#d97736" opacity="0.9" />
    <circle cx="28" cy="28" r="3" fill="#fff" opacity="0.5" />
  </svg>
);

const ResultScreen = ({ answers, onRestart }) => {
  const baseCats = useMemo(() => recommendCategories(answers), [answers]);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openType, setOpenType] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [wellnessSuiteSkipped, setWellnessSuiteSkipped] = useState(false);

  const showWellnessSuite = useMemo(
    () => shouldRecommendWellnessSuite(answers) && !wellnessSuiteSkipped,
    [answers, wellnessSuiteSkipped],
  );

  useEffect(() => {
    if (!baseCats.length && !(answers.medications || []).length) {
      setLoading(false);
      return;
    }
    const lifestyle = answers.diet_pref && answers.diet_pref !== 'none'
      ? [answers.diet_pref] : [];
    const seedSrc = JSON.stringify(answers);
    let seed = 0;
    for (let i = 0; i < seedSrc.length; i++) {
      seed = ((seed << 5) - seed + seedSrc.charCodeAt(i)) | 0;
    }
    seed = Math.abs(seed);

    axios.post(`${API}/kiosk/recommend`, {
      types: baseCats,
      lifestyle,
      seed,
      medications: answers.on_meds === 'yes' ? (answers.medications || []) : [],
      med_duration: answers.on_meds === 'yes' ? (answers.med_duration || null) : null,
    })
      .then((res) => setResponse(res.data))
      .catch(() => setResponse({ products: {}, ordered_types: baseCats, medication_insights: [] }))
      .finally(() => setLoading(false));
  }, [baseCats, answers]);

  const orderedTypes = response?.ordered_types || baseCats;
  const insights = response?.medication_insights || [];

  const recs = useMemo(() => {
    return orderedTypes.map((type) => {
      const live = response?.products?.[type];
      if (live?.top) {
        return {
          type, name: live.top.description, isLive: true,
          alternatives: live.alternatives || [],
        };
      }
      const mock = getTopProduct(type);
      return mock ? { type, name: mock.name, isLive: false, alternatives: [] } : null;
    }).filter(Boolean);
  }, [orderedTypes, response]);

  // Persist a snapshot of this plan once recs are ready, so the QR code can
  // link a customer's phone to a saved version of their recommendations.
  useEffect(() => {
    if (loading || planId) return;
    if (!recs.length && !insights.length && !shouldRecommendWellnessSuite(answers)) return;
    const wsEntry = shouldRecommendWellnessSuite(answers)
      ? [{ type: 'wellness_suite', name: WELLNESS_SUITE.name, isLive: true }]
      : [];
    const snapshot = {
      products: [...recs.map(({ type, name, isLive }) => ({ type, name, isLive })), ...wsEntry],
      insights: insights.map((ins) => ({
        medication_label: ins.medication_label,
        nutrient: ins.nutrient,
        reason: ins.reason,
        type: ins.type || null,
      })),
    };
    axios.post(`${API}/kiosk/plan`, snapshot)
      .then((r) => setPlanId(r.data.id))
      .catch(() => { /* QR card just won't render */ });
  }, [loading, recs, insights, planId, answers]);

  const planUrl = planId
    ? `${window.location.origin}/plan/${planId}`
    : null;

  return (
    <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 py-8 max-w-4xl mx-auto w-full">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="text-center mb-10"
      >
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#1f3d2b] text-white text-[10px] font-black uppercase tracking-[0.25em] mb-6">
          <Check size={12} strokeWidth={3} /> Plan Ready
        </div>
        <h1
          className="text-4xl sm:text-5xl text-[#0d1f15] mb-3 leading-[1.05] tracking-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          Your Personalized Plan
        </h1>
        <p
          className="text-base sm:text-lg text-[#5a7062] max-w-xl mx-auto"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {loading
            ? 'Finding the right products in store…'
            : `${recs.length} supplement${recs.length === 1 ? '' : 's'}${showWellnessSuite ? ' + Wellness Suite' : ''} recommended for you.`}
        </p>
      </motion.div>

      {/* Medication Impact Insights */}
      {insights.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.1 }}
          data-testid="medication-insights"
          className="mb-10 bg-white rounded-[2rem] p-6 sm:p-7 border border-[#1f3d2b]/10 shadow-sm"
        >
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#1f3d2b]/10 flex items-center justify-center">
              <Pill size={18} className="text-[#1f3d2b]" strokeWidth={2} />
            </div>
            <h3
              className="text-xl text-[#1f3d2b] tracking-tight"
              style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
            >
              Medication Impact Insights
            </h3>
          </div>
          <ul className="space-y-3 mb-4">
            {insights.map((ins, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span
                  className="flex-shrink-0 w-2 h-2 rounded-full mt-2"
                  style={{ background: TYPE_COLORS[ins.type] || '#1f3d2b' }}
                />
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.25em] text-[#5a7062] mb-0.5">
                    {ins.medication_label}
                  </div>
                  <div
                    className="text-[#0d1f15] leading-snug"
                    style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}
                  >
                    May lower your {ins.nutrient.toLowerCase()}
                  </div>
                  <div className="text-sm text-[#5a7062] mt-0.5">{ins.reason}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex items-start gap-2 text-xs text-[#5a7062] bg-[#f5e6d0] border border-[#d97736]/30 rounded-xl px-3 py-2">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-[#d97736]" strokeWidth={2.5} />
            <span>
              This is general information, not medical advice. Always discuss
              supplements with your physician or pharmacist before starting or
              stopping anything.
            </span>
          </div>
        </motion.div>
      )}

      {!loading && recs.length === 0 && !showWellnessSuite ? (
        <div className="text-center text-[#5a7062] py-12">
          No specific recommendations — your habits look great!
        </div>
      ) : (
        <motion.div
          initial="initial" animate="animate"
          variants={{ animate: { transition: { staggerChildren: 0.08 } } }}
          className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 auto-rows-fr"
        >
          {recs.map(({ type, name, isLive, alternatives }, i) => (
            <motion.div
              key={type}
              variants={{
                initial: { opacity: 0, y: 16 },
                animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
              }}
              data-testid={`kiosk-rec-${type}`}
              className="bg-white rounded-[2rem] p-6 sm:p-7 border border-[#1f3d2b]/8 shadow-xl shadow-[#1f3d2b]/[0.06] flex flex-col gap-4 relative overflow-hidden h-full"
            >
              <span
                className="absolute top-4 right-5 text-xs font-black uppercase tracking-[0.2em] text-[#1f3d2b]/50"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {String(i + 1).padStart(2, '0')}
              </span>

              <div className="flex gap-5 items-stretch flex-1">
                <div
                  className="flex-shrink-0 rounded-2xl flex items-center justify-center self-start"
                  style={{
                    background: `${TYPE_COLORS[type]}10`,
                    width: 112, height: 112,
                  }}
                >
                  <ProductBottle color={TYPE_COLORS[type]} size={84} />
                </div>

                <div className="flex-1 min-w-0">
                  <div
                    className="inline-block text-[10px] font-black uppercase tracking-[0.25em] mb-2 px-2.5 py-1 rounded-full"
                    style={{
                      background: `${TYPE_COLORS[type]}15`,
                      color: TYPE_COLORS[type],
                    }}
                  >
                    {TYPE_LABELS[type] || type}
                  </div>
                  <div
                    className="text-lg sm:text-xl text-[#0d1f15] leading-snug mb-2 tracking-tight"
                    style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}
                  >
                    {name}
                  </div>
                  <div
                    className="text-sm text-[#5a7062] leading-relaxed"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {TYPE_REASONS[type] || ''}
                  </div>
                  {isLive && (
                    <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#1f3d2b]/60">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#1f3d2b]" /> In stock
                    </div>
                  )}
                </div>
              </div>

              {alternatives && alternatives.length > 0 && (
                <button
                  data-testid={`alternatives-trigger-${type}`}
                  onClick={() => setOpenType(type)}
                  className="self-start mt-1 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-[#1f3d2b] active:text-[#0d1f15] transition-colors"
                >
                  See other options ({alternatives.length}) <ChevronRight size={14} strokeWidth={2.5} />
                </button>
              )}
            </motion.div>
          ))}

          {/* ── Wellness Suite recommendation card ─────────────────────── */}
          {showWellnessSuite && (
            <motion.div
              key="wellness_suite"
              variants={{
                initial: { opacity: 0, y: 16 },
                animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
              }}
              data-testid="kiosk-rec-wellness_suite"
              className="rounded-[2rem] flex flex-col gap-0 relative overflow-hidden h-full"
              style={{
                background: 'linear-gradient(145deg, #fef8f2 0%, #fdf3ea 100%)',
                border: '1px solid rgba(217,119,54,0.22)',
                boxShadow: '0 20px 60px rgba(217,119,54,0.12)',
              }}
            >
              {/* Amber top accent stripe */}
              <div
                className="h-1 w-full rounded-t-[2rem]"
                style={{ background: 'linear-gradient(90deg, #d97736 0%, #e8956a 50%, #d97736 100%)' }}
              />

              <div className="p-6 sm:p-7 flex flex-col gap-5 flex-1">
                {/* Skip button */}
                <button
                  data-testid="wellness-suite-skip"
                  onClick={() => setWellnessSuiteSkipped(true)}
                  className="absolute top-5 right-5 p-1.5 rounded-full transition-colors"
                  style={{ color: 'rgba(217,119,54,0.45)' }}
                  aria-label="Skip Wellness Suite recommendation"
                  onMouseEnter={e => e.currentTarget.style.color='#d97736'}
                  onMouseLeave={e => e.currentTarget.style.color='rgba(217,119,54,0.45)'}
                >
                  <X size={14} strokeWidth={2.5} />
                </button>

                {/* Header row: lotus icon + label + title */}
                <div className="flex gap-4 items-center">
                  <div
                    className="flex-shrink-0 rounded-2xl flex items-center justify-center"
                    style={{ background: 'rgba(217,119,54,0.12)', width: 72, height: 72 }}
                  >
                    <WellnessSuiteIcon />
                  </div>
                  <div className="flex-1 pr-5">
                    <div
                      className="inline-block text-[10px] font-black uppercase tracking-[0.25em] mb-1.5 px-2.5 py-1 rounded-full"
                      style={{ background: 'rgba(217,119,54,0.15)', color: '#c4612a' }}
                    >
                      Wellness Suite
                    </div>
                    <div
                      className="text-xl text-[#0d1f15] leading-snug tracking-tight"
                      style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700 }}
                    >
                      {WELLNESS_SUITE.name}
                    </div>
                  </div>
                </div>

                {/* Description — full width for readability */}
                <p
                  className="text-sm leading-relaxed"
                  style={{ fontFamily: "'Manrope', sans-serif", color: '#6b7c6e' }}
                >
                  {WELLNESS_SUITE.description}
                </p>

                {/* Feature chips with icons */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'Infrared Sauna', Icon: Flame },
                    { label: 'Red-Light Therapy', Icon: Sun },
                    { label: '4D Massage Chair', Icon: Sparkles },
                    { label: 'Aromatherapy', Icon: Wind },
                    { label: 'Relaxing Music', Icon: Music },
                  ].map(({ label, Icon }) => (
                    <span
                      key={label}
                      className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] px-2.5 py-1.5 rounded-full"
                      style={{
                        color: '#b8612a',
                        background: 'rgba(217,119,54,0.1)',
                        border: '1px solid rgba(217,119,54,0.2)',
                      }}
                    >
                      <Icon size={10} strokeWidth={2.5} />
                      {label}
                    </span>
                  ))}
                </div>

                {/* Price row + Book CTA */}
                <div className="flex flex-col gap-2.5 mt-auto pt-1">
                  <div className="flex items-center justify-between">
                    <div
                      className="inline-flex items-center gap-1.5 text-sm font-bold"
                      style={{ color: '#d97736' }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#d97736]" />
                      {WELLNESS_SUITE.price_label}
                    </div>
                    <a
                      href="/wellness-suite"
                      data-testid="wellness-suite-details-cta"
                      className="inline-flex items-center gap-0.5 text-[11px] font-bold uppercase tracking-[0.15em] transition-opacity hover:opacity-100"
                      style={{ color: 'rgba(217,119,54,0.7)' }}
                    >
                      View Details <ChevronRight size={12} strokeWidth={2.5} />
                    </a>
                  </div>
                  <a
                    href={WELLNESS_SUITE.booking_url}
                    data-testid="wellness-suite-book-cta"
                    className="w-full flex items-center justify-center gap-2 text-sm font-bold text-white py-3.5 rounded-2xl transition-all hover:opacity-90 active:scale-[0.98]"
                    style={{
                      background: 'linear-gradient(135deg, #d97736 0%, #c4612a 100%)',
                      boxShadow: '0 4px 20px rgba(217,119,54,0.35)',
                      fontFamily: "'Manrope', sans-serif",
                    }}
                  >
                    Book a Session <ArrowRight size={15} strokeWidth={2.5} />
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}

      {recs.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.4 }}
          className="mt-10 bg-white/60 backdrop-blur-md rounded-[1.75rem] p-6 sm:p-8 border-l-4 border-[#d97736]"
        >
          <h3
            className="text-lg text-[#1f3d2b] mb-2 tracking-tight"
            style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
          >
            Why this is recommended
          </h3>
          <p
            className="text-[#5a7062] leading-relaxed text-base"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            We matched your answers to the supplement categories that most
            directly support your goals, recovery, and lifestyle. Speak with a
            wellness advisor in store to confirm the right fit and dosage.
          </p>
        </motion.div>
      )}

      {planUrl && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut', delay: 0.5 }}
          data-testid="kiosk-qr-card"
          className="mt-10 bg-white rounded-[2rem] p-6 sm:p-8 border border-[#1f3d2b]/10 shadow-xl shadow-[#1f3d2b]/[0.06] flex flex-col sm:flex-row items-center gap-6 sm:gap-8"
        >
          <div className="flex-shrink-0 bg-white p-4 rounded-2xl border-2 border-[#1f3d2b]/15">
            <QRCodeSVG
              data-testid="kiosk-qr-svg"
              value={planUrl}
              size={176}
              fgColor="#0d1f15"
              bgColor="#ffffff"
              level="M"
              marginSize={0}
            />
          </div>
          <div className="text-center sm:text-left flex-1">
            <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.25em] text-[#1f3d2b] mb-3">
              <Smartphone size={14} strokeWidth={2.5} />
              Take it with you
            </div>
            <h3
              className="text-2xl sm:text-3xl text-[#0d1f15] mb-2 tracking-tight leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Scan to save your plan
            </h3>
            <p
              className="text-sm sm:text-base text-[#5a7062] leading-relaxed"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Open your phone&apos;s camera and point it at the code. Your full
              vitamin plan will open on your phone — keep it handy while you
              shop.
            </p>
          </div>
        </motion.div>
      )}

      <div className="mt-10 flex justify-center">
        <motion.button
          data-testid="kiosk-restart-button"
          onClick={onRestart}
          whileTap={{ scale: 0.96 }}
          className="inline-flex items-center gap-2 bg-transparent border-2 border-[#1f3d2b] text-[#1f3d2b] active:bg-[#1f3d2b]/5 font-bold rounded-full px-10 py-4 text-base tracking-wide transition-colors"
          style={{ fontFamily: "'Manrope', sans-serif", minHeight: 64 }}
        >
          <RotateCcw size={18} /> Start Over
        </motion.button>
      </div>

      <AlternativesSheet
        open={!!openType}
        type={openType}
        alternatives={
          (openType && recs.find((r) => r.type === openType)?.alternatives) || []
        }
        onClose={() => setOpenType(null)}
      />
    </div>
  );
};

export default ResultScreen;
